import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getLocalBasket, resetLocalBasket } from 'components/basket/localStore';
import { filterBasket } from 'components/basket/model/Basket';
import { locationApi } from 'components/location/locationApi';
import { menuApi } from 'components/menu/menuApi';
import { IEnrichedMenu, IEnrichedMenuWithModifierMaps, mapCategories } from 'components/menu/model/Menu';
import { OrderScenario } from 'components/order/model/Order';
import { getRequestDate } from 'lib/helpers';
import logger from 'lib/logger';
import { isDefined, isString } from 'lib/typeInference';
import { setBasket, resetBasketState, setBasketLocationId } from 'store/basket/basketSlice';
import { enqueueSnackbar } from 'notistack';
import type { ApplicationState } from 'store/store';

export interface MenuState {
    menu?: IEnrichedMenuWithModifierMaps;
    categories: IEnrichedMenu['categories'];
    error: string;
    isLoading: boolean;
}

export const initialState: MenuState = {
    menu: undefined,
    categories: [],
    error: '',
    isLoading: true
};

export const getMenu = createAsyncThunk(
    'menu/getMenu',
    async (
        {
            locationId,
            date,
            scenario = OrderScenario.PREORDER,
            basketChangeMessage = '',
            deliveryLocation
        }: {
            locationId: string;
            date?: string;
            scenario?: OrderScenario;
            basketChangeMessage?: string;
            deliveryLocation?: string;
        },
        { dispatch, getState, rejectWithValue }
    ) => {
        let myZone: string | undefined;
        if (scenario === OrderScenario.ORDER_TO_TABLE || scenario === OrderScenario.TABLE) {
            if (isDefined(deliveryLocation)) {
                try {
                    const result = await locationApi.getLocationZones(locationId, deliveryLocation);
                    if (isString(result?.zone)) {
                        myZone = result.zone;
                    }
                } catch (e: any) {
                    logger.error(e);
                }
            }
        }

        try {
            const params: Record<string, string> = {
                locationId,
                scenario,
                date: date || getRequestDate()
            };
            if (isDefined(myZone)) {
                params.zone = myZone;
            }
            const result = await menuApi.getMenu(params);
            const categories = mapCategories(result.categories, result);

            const nestedModifiersEnabled =
                (getState() as ApplicationState).settings?.settings?.menu?.nestedModifiersEnabled ?? false;
            const storedBasketState = getLocalBasket();
            if (storedBasketState) {
                if (storedBasketState.locationId === locationId) {
                    const { items: filteredItems, isValid } = filterBasket(
                        categories as any,
                        storedBasketState.items,
                        result,
                        nestedModifiersEnabled
                    );
                    if (!isValid) {
                        enqueueSnackbar(basketChangeMessage, {
                            variant: 'warning'
                        });
                    }
                    dispatch(setBasket({ ...storedBasketState, items: filteredItems } as any));
                } else {
                    resetLocalBasket();
                    dispatch(resetBasketState());
                }
            }
            dispatch(setBasketLocationId(locationId));
            return { menu: result, categories };
        } catch (e: any) {
            logger.error(e);
            dispatch(resetBasketState());
            return rejectWithValue(e);
        }
    }
);

const menuSlice = createSlice({
    name: 'menu',
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder
            .addCase(getMenu.pending, state => {
                state.isLoading = true;
            })
            .addCase(getMenu.fulfilled, (state, action) => {
                state.isLoading = false;
                state.error = '';
                state.menu = action.payload.menu as any;
                state.categories = action.payload.categories as any;
            })
            .addCase(getMenu.rejected, (state, action) => {
                state.isLoading = false;
                state.menu = undefined;
                state.error = action.error.message || '';
                state.categories = [];
            });
    }
});

export default menuSlice.reducer;
